import React, { useState, useEffect, useRef } from 'react';
import { Funnel, CaretDown, CalendarBlank, X } from '@phosphor-icons/react';
import { DateRange, Calendar as DateCalendar } from 'react-date-range';
import { es } from 'date-fns/locale';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import {
  FILTER_PANEL_CLASSES,
  FILTER_FIELD_CLASSES,
  FILTER_LABEL_CLASSES,
  FILTER_HEADER_CLASSES,
  FILTER_HEADER_TITLE_CLASSES,
} from './filterStyles';

/*Información*
 * Componente GenericFilters - Componente reutilizable para filtros colapsables
 * 
 * @param {Object} props
 * @param {Array} props.columns - Array de configuración de columnas de filtro
 * @param {Object} props.filters - Objeto con valores actuales de filtros
 * @param {Function} props.onFilterChange - Callback cuando cambia un filtro
 * @param {Function} props.onClearFilters - Callback para limpiar filtros
 * @param {boolean} props.defaultOpen - Si los filtros están abiertos por defecto
 * 
 * Ejemplo de configuración de columnas:
 * [
 *   {
 *     type: 'date',
 *     key: 'startDate',
 *     label: 'Fecha Inicio Desde',
 *     colSpan: 1,
 *     defaultValue: '2024-01-01' // Opcional: valor por defecto
 *   },
 *   {
 *     type: 'select',
 *     key: 'vehicleId',
 *     label: 'Vehículo',
 *     options: vehicles,
 *     optionValue: 'id',
 *     optionLabel: 'name',
 *     colSpan: 1
 *   }
 * ]
 */
const GenericFilters = ({ 
  columns = [], 
  filters = {}, 
  onFilterChange, 
  onClearFilters,
  defaultOpen = true,
  loading = false
}) => {
  const [showFilters, setShowFilters] = useState(defaultOpen);
  const [isNarrow, setIsNarrow] = useState(false);
  const [openRanges, setOpenRanges] = useState({});
  const [popupPositions, setPopupPositions] = useState({});
  const [popupWidths, setPopupWidths] = useState({});
  const calendarRefs = useRef({});
  const triggerRefs = useRef({});
  const sharedInputClasses = FILTER_FIELD_CLASSES;
  // Cerrar calendario al hacer click fuera
  useEffect(() => {
    function handleClickOutside(event) {
      Object.entries(openRanges).forEach(([key, isOpen]) => {
        if (isOpen && calendarRefs.current[key]) {
          if (!calendarRefs.current[key].contains(event.target) && !triggerRefs.current[key]?.contains(event.target)) {
            setOpenRanges(prev => ({ ...prev, [key]: false }));
          }
        }
      });
    }
    if (Object.values(openRanges).some(Boolean)) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openRanges]);

  // Inicializar filtros con valores por defecto al montar el componente
  useEffect(() => {
    const hasDefaults = columns.some(col => col.defaultValue !== undefined);
    const isEmpty = Object.keys(filters).length === 0 || Object.values(filters).every(v => 
      v === '' || (typeof v === 'object' && Object.values(v).every(nv => nv === ''))
    );
    
    if (hasDefaults && isEmpty) {
      const defaultFilters = {};
      columns.forEach(column => {
        if (column.defaultValue !== undefined) {
          if (column.isNested && column.nestedKey) {
            defaultFilters[column.key] = {
              [column.nestedKey]: column.defaultValue
            };
          } else {
            defaultFilters[column.key] = column.defaultValue;
          }
        } else {
          if (column.isNested && column.nestedKey) {
            defaultFilters[column.key] = { [column.nestedKey]: '' };
          } else {
            defaultFilters[column.key] = '';
          }
        }
      });
      onFilterChange(defaultFilters);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mediaQuery = window.matchMedia('(max-width: 640px)');
    const handleChange = (event) => setIsNarrow(event.matches);
    handleChange(mediaQuery);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  const handleFilterChange = (key, value, isNested = false, nestedKey = null) => {
    if (isNested && nestedKey) {
      onFilterChange({
        ...filters,
        [key]: {
          ...filters[key],
          [nestedKey]: value
        }
      });
    } else {
      const nextFilters = {
        ...filters,
        [key]: value
      };

      if ((key === 'startDate' || key === 'endDate') && nextFilters.startDate && nextFilters.endDate) {
        const start = parseLocalDate(nextFilters.startDate);
        const end = parseLocalDate(nextFilters.endDate);
        if (start && end && end < start) {
          const temp = nextFilters.startDate;
          nextFilters.startDate = nextFilters.endDate;
          nextFilters.endDate = temp;
        }
      }

      onFilterChange(nextFilters);
    }
  };

  const getFilterValue = (key, isNested = false, nestedKey = null) => {
    if (isNested && nestedKey) {
      return filters[key]?.[nestedKey] || '';
    }
    return filters[key] || '';
  };

  const handleClearFilters = () => {
    if (typeof onClearFilters === 'function') {
      onClearFilters();
      return;
    }

    const defaultFilters = {};
    columns.forEach(column => {
      if (column.isNested && column.nestedKey) {
        defaultFilters[column.key] = {
          [column.nestedKey]: column.defaultValue || ''
        };
      } else {
        defaultFilters[column.key] = column.defaultValue || '';
      }
    });
    onFilterChange(defaultFilters);
  };

  const getPopupPosition = (key, popupWidth = 320, popupHeight = 360, placement = 'auto') => {
    const trigger = triggerRefs.current[key];
    if (!trigger) {
      return { top: 80, left: 16 };
    }

    const rect = trigger.getBoundingClientRect();
    const margin = 8;
    let left = rect.left;
    let top = rect.bottom + margin;

    if (left + popupWidth > window.innerWidth - margin) {
      left = window.innerWidth - popupWidth - margin;
    }
    if (left < margin) {
      left = margin;
    }

    if (placement !== 'bottom' && top + popupHeight > window.innerHeight - margin) {
      top = rect.top - popupHeight - margin;
    }
    if (top < margin) {
      top = margin;
    }

    return { top, left };
  };

  const togglePopup = (key, popupWidth = 320, popupHeight = 360, matchTriggerWidth = false, placement = 'auto') => {
    const isOpen = Boolean(openRanges[key]);
    if (isOpen) {
      setOpenRanges(prev => ({ ...prev, [key]: false }));
      return;
    }

    const trigger = triggerRefs.current[key];
    const computedWidth = matchTriggerWidth && trigger
      ? Math.max(Math.round(trigger.getBoundingClientRect().width), 320)
      : popupWidth;
    const position = getPopupPosition(key, computedWidth, popupHeight, placement);
    setPopupPositions(prev => ({ ...prev, [key]: position }));
    setPopupWidths(prev => ({ ...prev, [key]: computedWidth }));
    setOpenRanges(prev => ({ ...prev, [key]: true }));
  };

  const parseLocalDate = (value) => {
    if (!value || typeof value !== 'string') return null;
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  const formatLocalDate = (date) => {
    if (!date || Number.isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDisplayDate = (value, locale = 'es-ES') => {
    const date = parseLocalDate(value);
    if (!date) return '';
    return date.toLocaleDateString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const renderFilterInput = (column) => {
    const {
      type,
      key,
      label,
      options = [],
      optionValue = 'id',
      optionLabel = 'name',
      colSpan = 1,
      isNested = false,
      nestedKey = null,
      showWhen,
      startKey,
      endKey,
      clearable = false,
      minDateKey,
      maxDateKey,
      popupPlacement = 'auto',
      criteriaKey,
      valueKey,
      criteriaOptions = []
    } = column;

    if (typeof showWhen === 'function' && !showWhen(filters)) {
      return null;
    }

    const inputClasses = sharedInputClasses;
    const selectClasses = sharedInputClasses;

    switch (type) {
      case 'date': {
        const dateValue = getFilterValue(key, isNested, nestedKey);
        const selectedDate = parseLocalDate(dateValue) || new Date();
        const isOpen = !!openRanges[key];
        const isAnchoredToField = popupPlacement === 'bottom';
        return (
          <div key={key} className={colSpan > 1 ? `md:col-span-${colSpan}` : ''} style={{ position: 'relative' }}>
            <label className={FILTER_LABEL_CLASSES}>
              {label}
            </label>
            <div className="relative date-picker-create">
              <button
                type="button"
                ref={el => { triggerRefs.current[key] = el; }}
                onClick={() => togglePopup(key, 320, 360, true, popupPlacement)}
                className={`${selectClasses} text-left flex items-center justify-between ${clearable && dateValue ? 'pr-10' : ''}`}
                aria-label={label}
                aria-expanded={isOpen}
              >
                <span>{dateValue ? formatDisplayDate(dateValue, 'es-AR') : 'dd/mm/aaaa'}</span>
                <CalendarBlank size={18} className="text-content-muted ml-2" />
              </button>
              {clearable && dateValue && (
                <button
                  type="button"
                  onClick={() => {
                    handleFilterChange(key, '', isNested, nestedKey);
                    setOpenRanges(prev => ({ ...prev, [key]: false }));
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  aria-label={`Limpiar ${label.toLowerCase()}`}
                  title={`Limpiar ${label.toLowerCase()}`}
                >
                  <X size={16} />
                </button>
              )}
              {isOpen && (
                <div
                  ref={el => { calendarRefs.current[key] = el; }}
                  className={`${isAnchoredToField ? 'absolute left-0 top-full mt-2' : 'fixed'} z-[9999] rounded-lg border border-line bg-surface-2 p-3 shadow-2xl`}
                  style={{
                    ...(!isAnchoredToField && {
                      top: `${popupPositions[key]?.top ?? 80}px`,
                      left: `${popupPositions[key]?.left ?? 16}px`
                    }),
                    width: `${popupWidths[key] ?? 320}px`,
                    maxWidth: '95vw'
                  }}
                >
                  <DateCalendar
                    date={selectedDate}
                    minDate={parseLocalDate(filters[minDateKey]) || undefined}
                    maxDate={parseLocalDate(filters[maxDateKey]) || undefined}
                    onChange={date => {
                      // Formato YYYY-MM-DD
                      // Usar año, mes, día local para evitar desfases de zona horaria
                      const formatted = formatLocalDate(date);
                      handleFilterChange(key, formatted, isNested, nestedKey);
                      setOpenRanges(prev => ({ ...prev, [key]: false }));
                    }}
                    locale={es}
                    color="#f5b731"
                    className="generic-date-range__picker compact-date-picker"
                  />
                </div>
              )}
            </div>
          </div>
        );
      }

      case 'select':
        return (
          <div key={key} className={colSpan > 1 ? `md:col-span-${colSpan}` : ''}>
            <label className={FILTER_LABEL_CLASSES}>
              {label}
            </label>
            <select
              value={getFilterValue(key, isNested, nestedKey)}
              onChange={(e) => handleFilterChange(key, e.target.value, isNested, nestedKey)}
              className={selectClasses}
            >
              {Array.isArray(options) && options.map((option) => (
                <option key={option[optionValue]} value={option[optionValue]}>
                  {option[optionLabel]}
                </option>
              ))}
            </select>
          </div>
        );

      case 'text':
        return (
          <div key={key} className={colSpan > 1 ? `md:col-span-${colSpan}` : ''}>
            <label className={FILTER_LABEL_CLASSES}>
              {label}
            </label>
            <input
              type="text"
              value={getFilterValue(key, isNested, nestedKey)}
              onChange={(e) => handleFilterChange(key, e.target.value, isNested, nestedKey)}
              placeholder={label}
              className={inputClasses}
            />
          </div>
        );

      case 'dateRange': {
        const rangeStartKey = startKey || `${key}Start`;
        const rangeEndKey = endKey || `${key}End`;
        const startValue = filters[rangeStartKey] || '';
        const endValue = filters[rangeEndKey] || '';
        const startDate = parseLocalDate(startValue) || new Date();
        const endDate = parseLocalDate(endValue) || startDate;
        const isOpen = Boolean(openRanges[key]);
        const isAnchoredToField = popupPlacement === 'bottom';

        const formatDateValue = (date) => {
          return formatLocalDate(date);
        };

        const displayValue = startValue && endValue
          ? startValue === endValue
            ? formatDisplayDate(startValue)
            : `${formatDisplayDate(startValue)} - ${formatDisplayDate(endValue)}`
          : startValue
            ? `Desde ${formatDisplayDate(startValue)}`
            : endValue
              ? `Hasta ${formatDisplayDate(endValue)}`
              : 'Seleccionar fechas';

        return (
          <div key={key} className={colSpan > 1 ? `md:col-span-${colSpan}` : ''} style={{position: 'relative'}}>
            <label className={FILTER_LABEL_CLASSES}>
              {label}
            </label>
            <button
              type="button"
              ref={el => { triggerRefs.current[key] = el; }}
              onMouseDown={(event) => {
                // El listener global de clic afuera se ejecuta en mousedown.
                // Evitamos que cierre antes de que este botón pueda alternar el popup.
                if (isOpen) event.stopPropagation();
              }}
              onClick={() => togglePopup(key, 500, 420, true, popupPlacement)}
              className={`${selectClasses} text-left`}
              aria-expanded={isOpen}
            >
              {displayValue}
            </button>

            {isOpen && (
              <div
                ref={el => { calendarRefs.current[key] = el; }}
                className={`${isAnchoredToField ? 'absolute left-0 top-full mt-2 w-full min-w-0 max-w-none' : 'fixed w-max min-w-[320px] max-w-[95vw]'} z-[9999] rounded-lg border border-line bg-surface-2 p-2 shadow-2xl`}
                style={isAnchoredToField
                  ? { boxShadow: '0 8px 32px 0 rgba(0,0,0,0.35)' }
                  : {
                    top: `${popupPositions[key]?.top ?? 80}px`,
                    left: `${popupPositions[key]?.left ?? 16}px`,
                    boxShadow: '0 8px 32px 0 rgba(0,0,0,0.35)'
                  }
                }
              >
                <DateRange
                  ranges={[
                    {
                      startDate,
                      endDate,
                      key: 'selection'
                    }
                  ]}
                  onChange={(ranges) => {
                    const nextStart = ranges.selection?.startDate || null;
                    let nextEnd = ranges.selection?.endDate || null;

                    if (nextStart && !nextEnd) {
                      nextEnd = nextStart;
                    }

                    if (nextStart && nextEnd && nextEnd < nextStart) {
                      nextEnd = nextStart;
                    }

                    const nextStartValue = formatDateValue(nextStart);
                    const nextEndValue = formatDateValue(nextEnd);

                    onFilterChange({
                      ...filters,
                      [rangeStartKey]: nextStartValue,
                      [rangeEndKey]: nextEndValue
                    });

                    if (nextStartValue && nextEndValue && nextStartValue !== nextEndValue) {
                      setOpenRanges(prev => ({ ...prev, [key]: false }));
                    }
                  }}
                  className="generic-date-range__picker"
                  months={1}
                  direction={isNarrow ? 'vertical' : 'horizontal'}
                  showDateDisplay={false}
                  moveRangeOnFirstSelection={false}
                  rangeColors={['#f5b731']}
                  locale={es}
                />
              </div>
            )}
          </div>
        );
      }

      case 'criteriaSearch': {
        const criterionFieldKey = criteriaKey || `${key}Criteria`;
        const valueFieldKey = valueKey || `${key}Value`;
        const currentCriterion = filters[criterionFieldKey] || (criteriaOptions[0]?.value ?? '');
        const currentValue = filters[valueFieldKey] || '';

        return (
          <div key={key} className={colSpan > 1 ? `md:col-span-${colSpan}` : ''} style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: '8px' }}>
            <div>
              <label className={FILTER_LABEL_CLASSES}>{label}</label>
              <select
                value={currentCriterion}
                onChange={(e) => {
                  onFilterChange({
                    ...filters,
                    [criterionFieldKey]: e.target.value,
                    [valueFieldKey]: ''
                  });
                }}
                className={selectClasses}
              >
                <option value="" disabled hidden>{label}</option>
                {criteriaOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={FILTER_LABEL_CLASSES}>&nbsp;</label>
              <input
                type="text"
                value={currentValue}
                onChange={(e) => onFilterChange({ ...filters, [valueFieldKey]: e.target.value })}
                placeholder="Buscar..."
                className={inputClasses}
              />
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className={FILTER_PANEL_CLASSES}>
      {/* Header colapsable */}
      <div
        className="px-6 py-4 flex items-center justify-between cursor-pointer"
        onClick={() => setShowFilters(!showFilters)}
        role="button"
        aria-expanded={showFilters}
      >
        <div className={`${FILTER_HEADER_CLASSES} ${loading ? 'animate-pulse' : ''}`}>
          <Funnel size={18} className="text-content" />
          <h4 className={FILTER_HEADER_TITLE_CLASSES}>Filtros</h4>
        </div>
        <CaretDown size={18} className={`text-content transition-transform ${showFilters ? 'rotate-180' : ''} ${loading ? 'animate-pulse' : ''}`} />
      </div>

      {/* Contenido de filtros (colapsable) */}
      {showFilters && (
        <div className="px-6 pb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            {columns.map(column => renderFilterInput(column))}
            
            {/* Botón Limpiar Filtros */}
            <div className="flex items-end">
              <div className="w-full">
                <button
                  onClick={handleClearFilters}
                  className={`${sharedInputClasses} whitespace-nowrap transition-colors hover:bg-surface-2`}
                >
                  Limpiar Filtros
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GenericFilters;
